import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo } from 'react';

import {
  BASE,
  NESTED_ROUTES,
  NestedRoutesType,
} from 'common/src/routes/BaseRoutes';
import { GENERATED_ROUTES } from 'common/src/routes/GeneratedRoutes';
import { generateNestedRouteWithId } from 'common/src/routes/helpers';
import {
  AlertSubtype,
  infoOnlySubtypes,
} from 'common/src/types/enums/AlertSubtype.enum';
import { AlertType } from 'common/src/types/enums/AlertType.enum';
import type { IAlert } from 'common/src/types/interfaces';

import useAuth from 'portal/src/auth/useAuth';
import { DataDisplay } from 'portal/src/components/billAutomation/FileUploadDetails/components';
import TimestampDisplay from 'portal/src/components/common/TimestampDisplay';

import AlertNotesSection from './notes/AlertNotesSection';
import { getAlertTypeColor } from './utils/alertTypeColors';
import { isResolutionRequiredSubtype } from './utils/alertsFiltering';

const getDataInsightButtonLabel = (url: string | null): string => {
  const viewResolutionLabel = 'View Resolution';

  if (!url) {
    return viewResolutionLabel;
  }

  if (url.includes(GENERATED_ROUTES.ADMINISTRATION.ACCOUNTS)) {
    return 'View Account';
  }

  if (url.includes(GENERATED_ROUTES.ADMINISTRATION.METERS)) {
    return 'View Meter';
  }

  if (url.includes(GENERATED_ROUTES.ADMINISTRATION.BILLS)) {
    if (url.includes('filters')) {
      return 'View Bills';
    }
    return 'View Bill';
  }

  return viewResolutionLabel;
};

const AlertDetailPane = ({
  alert,
  loading = false,
}: {
  alert: IAlert | null;
  loading?: boolean;
}) => {
  const isDataInsightAlert = alert?.type === AlertType.DataInsights;
  const { hasSystemAdminAccess } = useAuth();

  const alertUrl = useMemo(() => {
    const buildUrlWith = (
      base: 'admin' | 'automation',
      nestedPath: NestedRoutesType,
    ): string | null => {
      const basePath =
        base === 'admin' ? BASE.ADMINISTRATION : BASE.BILL_AUTOMATION;
      return generateNestedRouteWithId(
        basePath,
        nestedPath,
        alert?.sourceId?.[0],
      );
    };

    if (alert?.sourceId && alert.sourceId.length === 1 && alert.sourceId?.[0]) {
      switch (alert.subtype) {
        case AlertSubtype.AccountCreated:
          return buildUrlWith('admin', NESTED_ROUTES.ADMINISTRATION.ACCOUNTS);

        case AlertSubtype.AllocationNeedsAttention:
        case AlertSubtype.BillProcessed:
          return buildUrlWith('admin', NESTED_ROUTES.ADMINISTRATION.BILLS);

        case AlertSubtype.InvalidCredentials:
          return buildUrlWith(
            'automation',
            NESTED_ROUTES.BILL_AUTOMATION.CREDENTIALS,
          );

        case AlertSubtype.InsufficientQuality:
        case AlertSubtype.StatementRequiresReview:
          return buildUrlWith(
            'automation',
            NESTED_ROUTES.BILL_AUTOMATION.BILL_UPLOAD,
          );

        case AlertSubtype.MeterChangeout:
        case AlertSubtype.MeterCreated:
          return buildUrlWith('admin', NESTED_ROUTES.ADMINISTRATION.METERS);

        case AlertSubtype.PropertyCreated:
        case AlertSubtype.PropertyLinked:
          return buildUrlWith('admin', NESTED_ROUTES.ADMINISTRATION.FACILITIES);

        case AlertSubtype.AnomalousUsage:
        case AlertSubtype.CustomerAccountCreated:
        case AlertSubtype.CustomerAccountLinked:
        case AlertSubtype.FileIngested:
        case AlertSubtype.ErrorInBillImport:
        case AlertSubtype.ReportAvailable:
        case AlertSubtype.UrgentDataInsights:
        default:
        // Fallback to resolution url
      }
    }

    return alert?.resolutionUrl ?? null;
  }, [alert]);

  if (!alert && !loading) {
    return null;
  }

  const handleOpenInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Box>
      <Card
        sx={{
          position: 'sticky',
          top: 65,
          display: 'flex',
          flexDirection: 'column',
          height: 'fit-content',
          p: 2,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
            Alert Details
          </Typography>
        </Box>
        <DataDisplay
          label="Type"
          value={
            loading ? (
              <Skeleton variant="rounded" width={96} height={24} />
            ) : (
              <Chip
                label={alert?.type}
                sx={{
                  backgroundColor: getAlertTypeColor(alert!.type),
                  color: 'white',
                }}
                variant="filled"
                size="small"
              />
            )
          }
        />
        <DataDisplay
          label="Subtype"
          value={loading ? <Skeleton width={150} /> : alert?.subtype}
        />
        <DataDisplay
          label="Created"
          value={
            loading ? (
              <Skeleton width={180} />
            ) : (
              <TimestampDisplay date={alert?.createdAt} withDistanceToNow />
            )
          }
        />
        <DataDisplay
          label="Message"
          value={
            loading ? (
              <Stack spacing={0.5} sx={{ width: '100%' }}>
                <Skeleton width="95%" />
                <Skeleton width="85%" />
              </Stack>
            ) : (
              alert?.message
            )
          }
        />
        {!loading && isResolutionRequiredSubtype(alert?.subtype) && (
          <>
            <Divider sx={{ mt: 1 }} />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
              }}
            >
              <DataDisplay
                label="Status"
                value={
                  loading ? (
                    <Skeleton variant="rounded" width={96} height={24} />
                  ) : (
                    <Chip
                      label={alert?.resolved ? 'Resolved' : 'Unresolved'}
                      color={alert?.resolved ? 'success' : 'error'}
                      {...(!alert?.resolved &&
                        isDataInsightAlert && {
                          onDelete: () => {},
                          deleteIcon: (
                            <Tooltip
                              title={
                                'Properly addressed data insight alerts' +
                                ' can take up to 24 hours to show as “Resolved” unless' +
                                ' a refresh is triggered directly in the Data Insights page.'
                              }
                            >
                              <InfoOutlinedIcon />
                            </Tooltip>
                          ),
                        })}
                      variant="outlined"
                      size="small"
                    />
                  )
                }
              />
              {alertUrl && (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenInNewTab(alertUrl);
                  }}
                  variant="contained"
                  size="small"
                  color="secondary"
                  role="link"
                  sx={{
                    width: '100%',
                    maxWidth: '200px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  {alert.resolved
                    ? isDataInsightAlert
                      ? getDataInsightButtonLabel(alertUrl)
                      : `View${alert?.sourceType ? ` ${alert.sourceType}` : ''}`
                    : 'Resolve Here'}{' '}
                  <OpenInNewIcon fontSize="small" />
                </Button>
              )}
            </Box>
            <DataDisplay
              label="Resolution Reason"
              value={
                loading ? <Skeleton width={180} /> : alert?.resolutionReason
              }
            />
          </>
        )}
        {!loading &&
          infoOnlySubtypes.includes(alert?.subtype) &&
          alert?.subtype !== AlertSubtype.CustomerAccountCreated &&
          alert?.subtype !== AlertSubtype.CustomerAccountLinked && (
            <Tooltip
              describeChild
              title="No URL was provided for this alert"
              disableFocusListener={!!alertUrl}
              disableHoverListener={!!alertUrl}
            >
              <span>
                <Button
                  color="secondary"
                  onClick={(e) => {
                    e.preventDefault();
                    handleOpenInNewTab(alertUrl);
                  }}
                  size="small"
                  role="link"
                  endIcon={<OpenInNewIcon />}
                  sx={{ width: '200px' }}
                  variant="contained"
                  disabled={!alertUrl}
                >
                  View{' '}
                  {alert?.subtype === AlertSubtype.ReportAvailable
                    ? 'Report'
                    : (alert?.sourceType ?? '')}
                </Button>
              </span>
            </Tooltip>
          )}
        {alert && <AlertNotesSection key={alert.id} alertId={alert.id} />}
        {hasSystemAdminAccess() && (
          <>
            <Divider sx={{ mt: 1 }}>Metadata</Divider>
            <DataDisplay
              label="Source Type"
              value={loading ? <Skeleton width={150} /> : alert?.sourceType}
            />
            <DataDisplay
              direction={
                alert?.sourceId && alert.sourceId.length > 0 ? 'column' : 'row'
              }
              label="Source Subtypes"
              value={
                loading ? (
                  <Skeleton width={200} />
                ) : alert?.sourceId && alert.sourceId.length > 0 ? (
                  <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
                    {alert.sourceId.map((id, idx) => (
                      <Chip
                        key={idx}
                        label={id}
                        variant="outlined"
                        size="small"
                        sx={{ width: 'fit-content' }}
                      />
                    ))}
                  </Stack>
                ) : (
                  '-'
                )
              }
            />
          </>
        )}
      </Card>
    </Box>
  );
};

export default AlertDetailPane;
